import { EmptyState, Icon, Panel, Tag, type TagTone } from '@dayon/design-system';
import { formatCount, formatPesos, formatRate } from '@dayon/money';
import { formatDay } from '../format.ts';
import { sentence } from '../vocabulary.ts';
import type { BonusGrantRow } from '../types.ts';

/**
 * The bonus grants on this account, and the wagering state of each.
 *
 * ── THE NOTICE THIS TAB USED TO CARRY ──────────────────────────────────────
 *
 * "Bonus grants are not on this route … the profile route returns none of it,
 * so no bar is drawn." True of the route; not true of the platform.
 * `promo.bonus_grant` holds rows joined to `promo.promotion`, and
 * `GET /api/players/:id` now reads them under the same `players.view` that
 * `GET /api/promotions/grants` has always used for the same table.
 *
 * ── THE WARNING IN IT IS THE PART THAT SURVIVES ────────────────────────────
 *
 * "A progress meter is read as 'this bonus is 56% cleared', which is a claim
 * about whether a withdrawal may be released." So three rules govern the bar,
 * and each of them fires on real rows in this database:
 *
 *  1. NO DENOMINATOR, NO BAR. `wagering_required` is 0 for a FREE_SPINS grant
 *     until its bonus-funded winnings land and `promo.wagering` re-derives the
 *     requirement from them. Zero is UNDETERMINED, not met. The route sends
 *     `progress_bp: null` for it and nothing is drawn.
 *
 *  2. THE NUMBER IS NOT CLAMPED, only the bar is. `GET /api/promotions/grants`
 *     and the player's own rewards screen both clamp this with
 *     `least(10000, …)`, which turns 546% into "100%" — and "100%" beside an
 *     ACTIVE status is exactly the sentence the warning forbids. Every seeded
 *     ACTIVE grant on this platform is past its requirement (546%, 1234%,
 *     2296%), because `wagering_completed` counts every settled stake inside
 *     the grant's window. The true figure is printed; the bar is clamped
 *     because a bar cannot be wider than its track.
 *
 *  3. PAST THE REQUIREMENT IS NOT "CLEARED". A grant whose turnover has met its
 *     requirement is converted by the `promo.wagering` job, which posts
 *     PLAYER_BONUS → PLAYER_CASH and sets the status to COMPLETED. Until that
 *     has happened the money is still locked, and this tab says so in those
 *     words rather than showing a full bar an operator would read as a release.
 *
 * WHAT IS STILL NOT HERE, and is named rather than drawn: the individual rounds
 * behind `wagering_completed`. `promo.bonus_wagering` records them, one row per
 * `game.game_round`, and no route reads that table — so the total is shown and
 * the working behind it is not.
 */

const STATUS_TONE: Record<string, TagTone> = {
  ACTIVE: 'outline',
  COMPLETED: 'accent',
  FORFEITED: 'quiet',
  EXPIRED: 'quiet',
};

const KIND_LABEL: Record<string, string> = {
  DEPOSIT_MATCH: 'Deposit match',
  CASHBACK: 'Cashback',
  NO_DEPOSIT: 'No deposit',
  FREE_SPINS: 'Free spins',
};

/**
 * What the multiplier multiplies, which is not the same for every kind.
 *
 * "Multiplies the bonus AMOUNT — except for FREE_SPINS, where it multiplies the
 * winnings." Stated on the card because "20×" alone is two different promises.
 */
const multipliesWinnings = (kind: string) => kind === 'FREE_SPINS';

function WageringMeter({ grant }: { grant: BonusGrantRow }) {
  const required = BigInt(grant.wagering_required);
  const completed = BigInt(grant.wagering_completed);

  /* Rule 1 — no denominator, no bar. The route already decided this by sending
     null; the sentence differs by kind because the reason differs.

     The zero being described is `wagering_required`, and neither sentence below
     names it. A column name is not something an operator can look up, ask for
     or repeat to anybody, and they do not need it here: the requirement is
     already on this card in words — the multiplier sentence above, and the
     right-hand amount of the "X of Y" beside every bar that does get drawn. */
  if (grant.progress_bp === null) {
    return (
      <div className="text-subtle" style={{ fontSize: '12.5px' }}>
        {multipliesWinnings(grant.promotion_kind) ? (
          <>
            No bar: a free-spins requirement is{' '}
            {grant.wagering_multiplier
              ? `${formatCount(Number(grant.wagering_multiplier) / 100)}× `
              : ''}
            the bonus-funded <strong>winnings</strong>, and none have settled yet, so the turnover
            requirement is still 0. That is undetermined, not met — {formatPesos(completed)} of
            turnover has been counted against nothing.
          </>
        ) : (
          <>
            No bar: the turnover requirement is 0, so there is no denominator to draw against.{' '}
            {formatPesos(completed)} of turnover has been counted.
          </>
        )}
      </div>
    );
  }

  const bp = Number(grant.progress_bp);
  /* Rule 3 — met is not cleared, and only the ACTIVE case needs saying: a
     COMPLETED grant has already had its conversion posted. */
  const metButOpen = completed >= required && grant.status === 'ACTIVE';

  return (
    <>
      <div className="meter-row">
        <span className="tabular">{formatRate(bp)}</span>
        <span className="text-subtle" style={{ fontSize: '11px' }}>
          {formatPesos(completed)} of {formatPesos(required)}
        </span>
      </div>
      {/* Rule 2 — the bar is clamped, the number beside it is not. The bar alone
          is colour, and colour is not a value, so both are always rendered. */}
      <div
        className="meter"
        role="img"
        aria-label={`${formatRate(bp)} of the wagering requirement counted`}
      >
        <div className="meter-fill" style={{ width: `${Math.min(100, bp / 100)}%` }} />
      </div>
      {metButOpen ? (
        <div className="text-muted" style={{ fontSize: '12.5px', marginTop: 'var(--space-2)' }}>
          <strong>The requirement is met and the bonus is not yet released.</strong> The money is
          still in the bonus wallet: the wagering job is the one that
          moves it from the bonus account to cash and sets this row to Completed, and it has not
          done so.
          Until it does, this is not cash and not withdrawable.
        </div>
      ) : null}
    </>
  );
}

function GrantCard({ grant }: { grant: BonusGrantRow }) {
  const expired =
    grant.status === 'ACTIVE' &&
    grant.expires_at !== null &&
    new Date(grant.expires_at) < new Date();

  return (
    <div className="card" style={{ gap: 'var(--space-3)' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <strong>{grant.promotion_name}</strong>
            <Tag tone="neutral">{KIND_LABEL[grant.promotion_kind] ?? grant.promotion_kind}</Tag>
          </div>
          <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
            {grant.ref} · granted {formatDay(grant.created_at)}
            {grant.granted_by_name ? ` by ${grant.granted_by_name}` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="tabular">{formatPesos(BigInt(grant.amount))}</div>
          <Tag tone={STATUS_TONE[grant.status] ?? 'neutral'}>{sentence(grant.status)}</Tag>
        </div>
      </div>

      <WageringMeter grant={grant} />

      <div className="text-subtle" style={{ fontSize: '11px' }}>
        {grant.expires_at
          ? expired
            ? `Window closed ${formatDay(grant.expires_at)} — still Active, so the expiry job has not swept it back to the promotion budget yet.`
            : `Expires ${formatDay(grant.expires_at)}`
          : 'No expiry'}
        {grant.closed_at ? ` · closed ${formatDay(grant.closed_at)}` : ''}
      </div>

      {/* Funding is a fact about the LEDGER, and it is the only honest way to
          explain a ₱0 bonus wallet under an active grant.

          What the route is reporting is `funding_transaction_id IS NOT NULL`,
          and this notice no longer prints that name. It would tell an operator
          nothing they can act on, and the same absence is visible to them on
          the Transactions tab: a funded grant has a row there, this one has
          none, and that is something they can go and check for themselves. */}
      {!grant.funded ? (
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            <strong>No funding movement was posted for this grant.</strong> It carries no funding
            transaction, so the {formatPesos(BigInt(grant.amount))} above was never credited to the
            player&rsquo;s bonus account. That is why Balances can show a bonus of ₱0.00
            beside this card: the row records a promise and the wallet records the money, and for
            this grant only the first was written.
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function BonusesTab({ grants, playerRef }: { grants: BonusGrantRow[]; playerRef: string }) {
  return (
    <Panel title={`Bonuses on ${playerRef}`}>
      {grants.length === 0 ? (
        <EmptyState icon="gift" title="No bonus has been granted to this account">
          A real zero — bonus grants holds no row for{' '}
          {playerRef}. Not a failed read: the whole profile is one request, and a request that
          failed would have replaced this page with the reason rather than an empty panel.
        </EmptyState>
      ) : (
        <div className="stack">
          {grants.map((g) => (
            <GrantCard key={g.id} grant={g} />
          ))}

          {/* The running total is `wagering_completed`, and the amount beside
              each bar IS that column, so the sentence below can point at the
              screen instead of naming it. "Bonus grant" is the label the
              Transactions tab prints for the row, which is what an operator
              will be looking for when they go there — not the stored value. */}
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>A bar is turnover counted, never a verdict on a payout.</strong> The amount
              counted, shown beside each bar, is the sum of the settled stakes the wagering job has
              attributed to that grant — every stake inside its window, counted once across all
              grants — so it can and does run past the requirement before the conversion is posted.
              The figure beside each bar is the true ratio and is deliberately not clamped to 100%.
              The MONEY a grant moved is on the Transactions tab as a Bonus grant row; the
              individual rounds behind the total are in a per-grant wagering record nothing here can
              read, so the working is not shown here.
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}
